import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ResultadosEncuesta from "../components/ResultadosEncuesta";
import SurveyKeyboardHint from "../components/SurveyKeyboardHint";
import SurveyScaleOptions from "../components/SurveyScaleOptions";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useSurveyKeyboard } from "../hooks/useSurveyKeyboard";
import {
  resolveSurveyToken,
  fetchPreguntas,
  fetchPerfil,
  guardarRespuestas,
  getRespuestas,
} from "../utils/decodeSurveyToken";
import { resolveSurveyResume } from "../utils/resolveSurveyResume";
import { preguntaTexto } from "../utils/preguntaTexto";
import logoInstituto from "../assets/logo-instituto-negro.png";
import styles from "../styles/encuesta4c.module.css";

const STEP_WELCOME = "welcome";
const STEP_INSTRUCTIONS = "instructions";
const STEP_QUIZ = "quiz";
const STEP_THANKS = "thanks";

const TOTAL_PREGUNTAS = 40;

function Encuesta4C() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [step, setStep] = useState(null);
  const [ids, setIds] = useState(null);
  const [tokenError, setTokenError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [preguntas, setPreguntas] = useState([]);
  const [loadingPreguntas, setLoadingPreguntas] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState(() => Array(TOTAL_PREGUNTAS).fill(null));
  const [perfil, setPerfil] = useState(null);
  const [loadingPerfil, setLoadingPerfil] = useState(false);
  const [showKeyboardHint, setShowKeyboardHint] = useState(true);
  const [hideKeyboardHint, setHideKeyboardHint] = useState(false);
  const [highlightedOption, setHighlightedOption] = useState(null);
  const year = new Date().getFullYear();

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get("token");

      if (!token) {
        if (!cancelled) setTokenError(true);
        if (!cancelled) setLoading(false);
        return;
      }

      const decoded = await resolveSurveyToken(token);
      if (cancelled) return;
      if (!decoded) {
        setTokenError(true);
      } else {
        setIds(decoded);
        setTokenError(false);
      }
      setLoading(false);
    };

    setLoading(true);
    run();
    return () => {
      cancelled = true;
    };
  }, [location.search]);

  useEffect(() => {
    if (!ids) return;
    let cancelled = false;
    setLoadingPreguntas(true);
    Promise.all([fetchPreguntas(), getRespuestas(ids)])
      .then(([list, savedList]) => {
        if (cancelled) return;
        setPreguntas(list);
        const resume = resolveSurveyResume(list, savedList, TOTAL_PREGUNTAS);
        if (resume.hasProgress) {
          setAnswers(resume.answers);
          setCurrentQuestionIndex(resume.questionIndex);
          setStep(resume.isComplete ? STEP_THANKS : STEP_QUIZ);
        } else {
          setStep(STEP_WELCOME);
        }
      })
      .catch(() => {
        if (!cancelled) setStep(STEP_WELCOME);
      })
      .finally(() => {
        if (!cancelled) setLoadingPreguntas(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ids]);

  useEffect(() => {
    if (step !== STEP_THANKS || !ids) return;
    let cancelled = false;
    setLoadingPerfil(true);
    fetchPerfil(ids)
      .then((data) => {
        if (!cancelled) setPerfil(data);
      })
      .finally(() => {
        if (!cancelled) setLoadingPerfil(false);
      });
    return () => {
      cancelled = true;
    };
  }, [step, ids]);

  const totalPreguntas = preguntas.length;
  const currentPregunta = preguntas[currentQuestionIndex];

  useEffect(() => {
    setHighlightedOption(answers[currentQuestionIndex] ?? null);
  }, [currentQuestionIndex, answers]);

  const dismissKeyboardHint = useCallback(() => {
    setHideKeyboardHint((hiding) => {
      if (hiding) return hiding;
      window.setTimeout(() => setShowKeyboardHint(false), 450);
      return true;
    });
  }, []);

  const handleAnswer = useCallback(
    async (value) => {
      dismissKeyboardHint();
      const pregunta = preguntas[currentQuestionIndex];
      const id_pregunta = pregunta?.id_pregunta;
      const total = preguntas.length;

      if (ids && id_pregunta != null) {
        const result = await guardarRespuestas(ids, [{ id_pregunta, valor: value }]);
        setAnswers((prev) => {
          const next = [...prev];
          next[currentQuestionIndex] = value;
          return next;
        });
        if (currentQuestionIndex < total - 1) {
          setCurrentQuestionIndex((i) => i + 1);
        } else if (result.completado || currentQuestionIndex === total - 1) {
          setStep(STEP_THANKS);
        }
        return;
      }

      setAnswers((prev) => {
        const next = [...prev];
        next[currentQuestionIndex] = value;
        return next;
      });
      if (currentQuestionIndex < total - 1) {
        setCurrentQuestionIndex((i) => i + 1);
      } else {
        setStep(STEP_THANKS);
      }
    },
    [ids, preguntas, currentQuestionIndex, dismissKeyboardHint]
  );

  const handleConfirm = useCallback(() => {
    if (highlightedOption === null || highlightedOption === undefined) return;
    return handleAnswer(highlightedOption);
  }, [highlightedOption, handleAnswer]);

  const handlePrev = useCallback(() => {
    setCurrentQuestionIndex((i) => (i > 0 ? i - 1 : i));
  }, []);

  useSurveyKeyboard({
    enabled: step === STEP_QUIZ && !loadingPreguntas && !!currentPregunta,
    onSelect: setHighlightedOption,
    onConfirm: handleConfirm,
    onPrev: handlePrev,
  });

  if (tokenError) {
    return (
      <div className={`${styles.root} ${styles.layout}`}>
        <header className={styles.header}>
          <img src={logoInstituto} alt={t("common.logoAlt")} className={styles.logo} />
        </header>
        <main className={styles.main}>
          <div className={styles.invalidWrap}>
            <h1 className={styles.invalidTitle}>{t("invalid.title")}</h1>
            <p className={styles.invalidText}>{t("invalid.text")}</p>
          </div>
        </main>
        <footer className={styles.footer}>{t("common.footer", { year })}</footer>
        <LanguageSwitcher />
      </div>
    );
  }

  if (loading || (!ids && !tokenError) || (ids && step == null)) {
    return (
      <div className={`${styles.root} ${styles.layout}`}>
        <header className={styles.header}>
          <img src={logoInstituto} alt={t("common.logoAlt")} className={styles.logo} />
        </header>
        <main className={styles.main}>
          <p className={styles.invalidText}>{t("common.loading")}</p>
        </main>
        <footer className={styles.footer}>{t("common.footer", { year })}</footer>
        <LanguageSwitcher />
      </div>
    );
  }

  const handleWelcomeNext = () => setStep(STEP_INSTRUCTIONS);
  const handleInstructionsNext = () => setStep(STEP_QUIZ);

  const progressPct =
    totalPreguntas > 0 ? ((currentQuestionIndex + 1) / totalPreguntas) * 100 : 0;
  const progressWidth = `${progressPct}%`;
  const selectedOption = highlightedOption;

  const nombreCompleto = ids?.nombre_usuario || t("common.userFallback");
  const nombreEquipo = ids?.nombre_equipo || null;
  const mainClass = step === STEP_THANKS ? styles.mainResults : styles.main;

  return (
    <div className={`${styles.root} ${styles.layout}`}>
      <header className={styles.header}>
        <img src={logoInstituto} alt={t("common.logoAlt")} className={styles.logo} />
        <div className={styles.headerUser}>
          {nombreEquipo ? (
            <>
              <span className={styles.headerUserName}>{nombreCompleto}</span>
              <span className={styles.headerSeparator}> – </span>
              <span className={styles.headerTeamName}>{nombreEquipo}</span>
            </>
          ) : (
            <span className={styles.headerUserName}>{nombreCompleto}</span>
          )}
        </div>
      </header>

      <main className={mainClass}>
        {step === STEP_WELCOME && (
          <div
            className={`${styles.stepTransition} ${styles.welcomeWrap}`}
            role="region"
            aria-label={t("welcome.aria")}
          >
            <div>
              <h1 className={styles.welcomeTitle}>
                {t("welcome.title")}{" "}
                <span className={styles.welcomeTitleHighlight}>
                  {t("welcome.titleHighlight")}
                </span>
              </h1>
              <div className={styles.welcomeIntro}>
                <p>{t("welcome.hello")}</p>
                <p>{t("welcome.p1")}</p>
                <p>{t("welcome.p2")}</p>
                <p>{t("welcome.p3")}</p>
              </div>
            </div>
            <div
              className={`${styles.card} ${styles.cardShadow}`}
              style={{
                marginTop: "3rem",
                maxWidth: "28rem",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleWelcomeNext}
              >
                {t("welcome.cta")}
              </button>
            </div>
          </div>
        )}

        {step === STEP_INSTRUCTIONS && (
          <div
            className={`${styles.stepTransition} ${styles.instructionsWrap}`}
            role="region"
            aria-label={t("instructions.aria")}
          >
            <div
              className={`${styles.card} ${styles.cardShadow}`}
              style={{ padding: "2rem" }}
            >
              <div className={styles.instructionsIconWrap}>
                <svg
                  className={styles.instructionsIcon}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className={styles.instructionsTitle}>{t("instructions.title")}</h2>
              <div className={styles.instructionsText}>
                <p>{t("instructions.p1")}</p>
                <p>
                  {t("instructions.p2Before")}{" "}
                  <strong>{t("instructions.scaleZero")}</strong>{" "}
                  {t("instructions.p2Mid")}{" "}
                  <strong>{t("instructions.scaleSix")}</strong>{" "}
                  {t("instructions.p2After")}
                </p>
                <p>
                  {t("instructions.p3Before")} <strong>0</strong>{" "}
                  {t("instructions.p3Mid")} <strong>6</strong>{" "}
                  {t("instructions.p3After")}
                </p>
              </div>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleInstructionsNext}
              >
                {t("instructions.cta")}
              </button>
            </div>
          </div>
        )}

        {step === STEP_QUIZ && (
          <div
            className={`${styles.stepTransition} ${styles.quizWrap}`}
            role="region"
            aria-label={t("quiz.aria")}
          >
            <div className={styles.progressSection}>
              <div className={styles.progressHeader}>
                <div>
                  <div className={styles.progressLabel}>{t("quiz.progressLabel")}</div>
                  <h2 className={styles.progressTitle}>
                    {t("quiz.questionOf", {
                      current: currentQuestionIndex + 1,
                      total: totalPreguntas || TOTAL_PREGUNTAS,
                    })}
                  </h2>
                </div>
                <span className={styles.progressPct}>
                  {totalPreguntas > 0 ? progressPct.toFixed(1) : 0}%
                </span>
              </div>
              <div className={styles.progressBarTrack}>
                <div
                  className={styles.progressBarFill}
                  style={{ width: progressWidth }}
                />
              </div>
            </div>

            <div
              className={`${styles.card} ${styles.cardShadow} ${styles.cardMarginTop}`}
            >
              {loadingPreguntas || !currentPregunta ? (
                <p className={styles.invalidText}>{t("quiz.loadingQuestions")}</p>
              ) : (
                <>
                  <SurveyKeyboardHint
                    visible={showKeyboardHint}
                    hiding={hideKeyboardHint}
                  />
                  <p className={styles.questionLead}>{t("quiz.lead")}</p>
                  <p className={styles.questionText}>
                    {preguntaTexto(currentPregunta, i18n.language)}
                  </p>
                  <p className={styles.optionLabel}>{t("quiz.selectOption")}</p>
                  <SurveyScaleOptions
                    selectedOption={selectedOption}
                    onSelect={(value) => {
                      setHighlightedOption(value);
                      handleAnswer(value);
                    }}
                  />
                </>
              )}
            </div>

            <div className={styles.quizNav}>
              <button
                type="button"
                className={styles.btnPrev}
                disabled={currentQuestionIndex === 0}
                onClick={handlePrev}
                aria-label={t("quiz.prevAria")}
              >
                <svg
                  className={styles.btnPrevIcon}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                <span className={styles.btnPrevLabel}>{t("quiz.prev")}</span>
              </button>
            </div>
          </div>
        )}

        {step === STEP_THANKS && (
          <ResultadosEncuesta
            ids={ids}
            perfil={perfil}
            loadingPerfil={loadingPerfil}
          />
        )}
      </main>

      <footer className={styles.footer}>{t("common.footer", { year })}</footer>
      <LanguageSwitcher />
    </div>
  );
}

export default Encuesta4C;
